import { AnimTrack, KeyEasing } from './anim-track';
import { AnimTrackEditOp } from './edit-ops';
import { Element, ElementType } from './element';
import { Events } from './events';

type TrackTargetKind = 'camera' | 'sceneCamera' | 'splatController' | 'meshController';

type TrackTargetInfo = {
    kind: TrackTargetKind;
    label: string;
    detail: string;
    isFallback: boolean;
    track: AnimTrack | null;
};

const getElementLabel = (selection: Element | null | undefined) => {
    const name = String((selection as any)?.name ?? '').trim();
    if (selection?.type === ElementType.splat) {
        return name ? `Splat \"${name}\"` : 'Selected splat';
    }
    if (selection?.type === ElementType.model) {
        return name ? `Mesh \"${name}\"` : 'Selected mesh';
    }
    if (selection?.type === ElementType.helper) {
        return name ? `Scene camera \"${name}\"` : 'Selected scene camera';
    }
    return name ? `Selection \"${name}\"` : 'Current selection';
};

/**
 * Manages the active animation track and provides undo-wrapped
 * key operations. Resolves which track the user is interacting
 * with and ensures all mutations are undoable.
 *
 * When a scene camera is selected, its per-camera track is the active track.
 * Otherwise, the global camera animation track is used.
 */
const registerTrackManagerEvents = (events: Events) => {
    const getActiveTrackTarget = (): TrackTargetInfo => {
        const selection = events.invoke('selection');
        if (selection && (selection as any).sceneCameraData) {
            const track = (selection as any).sceneCameraData.animTrack ?? null;
            const label = getElementLabel(selection as Element);
            return {
                kind: 'sceneCamera',
                label,
                detail: 'Scene camera track',
                isFallback: false,
                track
            };
        }

        if ((selection as Element | null)?.type === ElementType.splat) {
            const track = events.invoke('elementAnim.track', selection as Element) ?? null;
            if (track) {
                return {
                    kind: 'splatController',
                    label: getElementLabel(selection as Element),
                    detail: 'Splat controller track',
                    isFallback: false,
                    track
                };
            }
        }

        if ((selection as Element | null)?.type === ElementType.model) {
            const track = events.invoke('elementAnim.track', selection as Element) ?? null;
            if (track) {
                return {
                    kind: 'meshController',
                    label: getElementLabel(selection as Element),
                    detail: 'Mesh controller track',
                    isFallback: false,
                    track
                };
            }
        }

        const fallbackTrack = events.invoke('camera.animTrack') ?? null;
        const fallbackSelection = selection as Element | null;
        const fallbackSource = fallbackSelection ? `${getElementLabel(fallbackSelection)} has no element track` : 'No element selected';
        return {
            kind: 'camera',
            label: 'Global camera',
            detail: `Global camera track (${fallbackSource})`,
            isFallback: !!fallbackSelection
                && fallbackSelection.type !== ElementType.camera
                && !(fallbackSelection as any).sceneCameraData,
            track: fallbackTrack
        };
    };

    const getActiveTrack = (): AnimTrack | null => getActiveTrackTarget().track;

    // When selection changes, the active track may change — notify timeline to rebuild.
    events.on('selection.changed', () => {
        events.fire('track.keysLoaded');
    });

    events.function('track.targetInfo', () => {
        return getActiveTrackTarget();
    });

    // Helper: execute an edit on the active track wrapped in undo.
    // The editFn must return true if it modified the track, false if it was a no-op.
    const trackEdit = (name: string, editFn: (track: AnimTrack) => boolean) => {
        const track = getActiveTrack();
        if (!track) return;
        const before = track.snapshot();
        if (!editFn(track)) return;
        const after = track.snapshot();
        events.fire('edit.add', new AnimTrackEditOp(name, track, before, after), true);
    };

    // Get keys from active track
    events.function('track.keys', () => {
        const track = getActiveTrack();
        return track ? track.keys : [];
    });

    // Add key to active track
    events.on('track.addKey', (frame?: number) => {
        const keyFrame = frame ?? events.invoke('timeline.frame');
        trackEdit('addKey', track => track.addKey(keyFrame));
    });

    // Remove key from active track
    events.on('track.removeKey', (frame?: number) => {
        const keyFrame = frame ?? events.invoke('timeline.frame');
        trackEdit('removeKey', track => track.removeKey(keyFrame));
    });

    // Move key in active track
    events.on('track.moveKey', (fromFrame: number, toFrame: number) => {
        trackEdit('moveKey', track => track.moveKey(fromFrame, toFrame));
    });

    // Copy key in active track
    events.on('track.copyKey', (fromFrame: number, toFrame: number) => {
        trackEdit('copyKey', track => track.copyKey(fromFrame, toFrame));
    });

    // Get easing for a key
    events.function('track.getKeyEasing', (frame: number): KeyEasing | undefined => {
        const track = getActiveTrack();
        return track?.getKeyEasing(frame);
    });

    // Set easing for a key
    events.on('track.setKeyEasing', (frame: number, easing: KeyEasing | undefined) => {
        trackEdit('setKeyEasing', track => track.setKeyEasing(frame, easing));
    });
};

export { registerTrackManagerEvents };
export type { TrackTargetInfo, TrackTargetKind };
